import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { httpErrorMessage } from '../../../core/utils/http-error-message';

@Component({
  selector: 'app-reset-password',
  imports: [FormsModule, RouterLink],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
})
export class ResetPassword {
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  password = '';
  passwordConfirmation = '';
  email = this.route.snapshot.queryParamMap.get('email') ?? '';
  otp = '';
  showPassword = false;
  showConfirmation = false;
  readonly portal = this.resolvePortal(this.route.snapshot.queryParamMap.get('portal'));
  readonly otpSent = this.route.snapshot.queryParamMap.get('sent') === '1';
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  onSubmit(): void {
    const normalizedEmail = this.email.trim().toLowerCase();
    const normalizedOtp = this.otp.replace(/\D/g, '').slice(0, 6);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      this.errorMessage = 'Enter a valid email address.';
      return;
    }
    if (normalizedOtp.length !== 6) {
      this.errorMessage = 'Enter the 6-digit OTP sent to your email.';
      return;
    }
    if (this.password.length < 8) {
      this.errorMessage = 'Password must be at least 8 characters.';
      return;
    }
    if (this.password !== this.passwordConfirmation) {
      this.errorMessage = 'New password and confirmation do not match.';
      return;
    }
    this.email = normalizedEmail;
    this.otp = normalizedOtp;
    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.auth
      .resetPassword({
        email: normalizedEmail,
        otp: normalizedOtp,
        password: this.password,
        confirmPassword: this.passwordConfirmation,
        accountType: this.portal === 'admin' ? 'Platform' : 'Business',
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          this.successMessage = response.message || 'Password updated.';
          setTimeout(() => void this.router.navigate([this.loginRoute], {
            queryParams: { email: normalizedEmail, reset: 1 },
          }), 900);
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to reset password. Check the OTP and try again.');
        },
      });
  }

  otpInputChanged(): void {
    this.otp = this.otp.replace(/\D/g, '').slice(0, 6);
  }

  get loginRoute(): string {
    return this.portal === 'admin' ? '/admin-login' : this.portal === 'franchise' ? '/franchise-login' : '/login';
  }

  private resolvePortal(value: string | null): 'admin' | 'business' | 'franchise' {
    return value === 'admin' || value === 'franchise' ? value : 'business';
  }
}
